import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, API } from '../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Database, Loader2, Download, Upload } from 'lucide-react';

export default function DataBackup() {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [importMode, setImportMode] = useState('upsert');

  useEffect(() => {
    apiGet('/settings/backup/collections')
      .then((d) => { setCollections(d.collections || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const reload = () => {
    setLoading(true);
    apiGet('/settings/backup/collections')
      .then((d) => { setCollections(d.collections || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      // Accept either the export envelope { collection, data: [...] } or a raw array
      let collection = parsed.collection;
      let data = parsed.data || parsed;
      if (!Array.isArray(data)) throw new Error('JSON must contain a "data" array (or be a raw array).');
      if (!collection) {
        // Try to infer from filename: e.g. "users-2026-05-12.json"
        const m = file.name.match(/^([a-z_]+)/i);
        if (m) collection = m[1];
        if (!collection) throw new Error('Could not infer collection name. Use the file exported from /settings/data-backup.');
      }
      const res = await apiPost('/settings/backup/import', { collection, data, mode: importMode });
      setImportResult({ ...res, fileName: file.name });
      const errMsg = res.error_count > 0 ? ` · ${res.error_count} errors` : '';
      toast.success(`${collection}: ${res.inserted} inserted · ${res.updated} updated · ${res.skipped} skipped${errMsg}`, { duration: 6000 });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const exportCollection = async (collection) => {
    setDownloading(collection);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API}/settings/backup/export/${collection}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Export failed');
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${collection}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.count} rows from ${collection}`);
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
    setDownloading(null);
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  const totalRows = collections.reduce((a, c) => a + c.count, 0);

  return (
    <div className="p-6 md:p-8" data-testid="data-backup-page">
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#1F7A6E] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader overline="Settings · Data & Backup" title="Data & Backup" subtitle={`Download any collection as JSON. Total: ${totalRows.toLocaleString()} rows across ${collections.length} collections.`} icon={Database} />

      {/* Import panel — migrate data from another environment (e.g. preview → production) */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mt-6" data-testid="import-panel">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-sm bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"><Upload size={16} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base mb-0.5">Import from JSON</div>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">Upload a JSON file exported from another environment (e.g. preview → production). The file should contain <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">{`{ collection, data: [...] }`}</code> as produced by the Export buttons below.</p>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-xs uppercase tracking-wider font-display font-bold text-slate-500">Mode</label>
              <select value={importMode} onChange={(e) => setImportMode(e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white" data-testid="import-mode">
                <option value="upsert">Upsert (merge by natural key)</option>
                <option value="skip-existing">Skip existing rows</option>
                <option value="wipe-and-load">Wipe & Load (DANGEROUS · super-admin only)</option>
              </select>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                onChange={handleFile}
                disabled={importing}
                className="hidden"
                data-testid="import-file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50 font-display font-bold uppercase tracking-wider"
                data-testid="import-trigger-btn"
              >
                {importing ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                {importing ? 'Importing…' : 'Choose JSON to Import'}
              </button>
            </div>
            {importResult && (
              <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-sm p-3 space-y-2" data-testid="import-result">
                <div><b>{importResult.fileName}</b> → <b>{importResult.collection}</b>: {importResult.rows_received} rows received · <b>{importResult.inserted}</b> inserted · <b>{importResult.updated}</b> updated · {importResult.skipped} skipped{importResult.error_count ? <> · <b className="text-red-700">{importResult.error_count} errors</b></> : null}</div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <details className="text-red-800 bg-red-50 border border-red-200 rounded-sm p-2">
                    <summary className="cursor-pointer font-semibold">{importResult.error_count} import errors — click to expand</summary>
                    <ul className="mt-2 space-y-1 font-mono text-[10px]">
                      {importResult.errors.map((er, i) => (
                        <li key={i} className="border-t border-red-200 pt-1">
                          <div>{Object.entries(er.row_keys || {}).map(([k, v]) => `${k}=${v}`).join(' · ')}</div>
                          <div className="text-red-700">{er.error}</div>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mt-4">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-display">
            <tr>
              <th className="text-left py-2.5 px-4">Collection</th>
              <th className="text-right py-2.5 px-4 w-32">Rows</th>
              <th className="text-right py-2.5 px-4 w-40">Action</th>
            </tr>
          </thead>
          <tbody>
            {collections.map((c) => (
              <tr key={c.collection} className="border-t border-slate-100" data-testid={`bkp-${c.collection}`}>
                <td className="py-2.5 px-4 font-mono">{c.collection}</td>
                <td className="py-2.5 px-4 text-right font-semibold">{c.count.toLocaleString()}</td>
                <td className="py-2.5 px-4 text-right">
                  <button
                    onClick={() => exportCollection(c.collection)}
                    disabled={c.count === 0 || downloading === c.collection}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:border-[#1F7A6E] hover:bg-teal-50/50 disabled:opacity-40 disabled:cursor-not-allowed font-display font-bold uppercase tracking-wider"
                    data-testid={`export-${c.collection}`}
                  >
                    {downloading === c.collection ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                    {downloading === c.collection ? 'Exporting…' : 'Export JSON'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-800">
        <strong>Tip:</strong> Exports are JSON files including a header with collection name, export timestamp, and row count. Restore is not supported from the UI — contact admin to import.
      </div>
    </div>
  );
}
